import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import ArrowLeftSLine from '@/assets/icons/ri_arrow_left_s_line.svg?component';
import ArrowRightSLine from '@/assets/icons/ri_arrow_right_s_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';

import DayScrubber from './DayScrubber/DayScrubber';
import { scrollPage, scrollToDay } from './railScroll';
import { useKeepCurrentCardInView, useLazyThumbnails } from './useFilmstripCards';
import { useRailGestures } from './useRailGestures';
import { useRailView } from './useRailView';

import styles from './Filmstrip.module.scss';

export interface FilmstripProps {
    /** Cards, each day's run started by a DayMarker. */
    children?: ReactNode;
    /** Every day with images, oldest first; the scrubber has one tick per day. */
    days: { day: string; count: number }[];
    /** Loads a day's images: what the old day slider called when it was released. */
    onDayChange: (day: string) => void;
    currentDay?: string;
    currentImageId?: string;
    /** Bioscope strips hold thousands of frames, so their thumbnails load closer in. */
    denseThumbnails?: boolean;
}

/**
 * The strip of thumbnails under the image and the day scrubber under it, one navigator. The
 * strip scrolls sideways by touch, trackpad, wheel or mouse drag, with no scrollbar of its
 * own: edge fades and page buttons show only where there is more to see, and the scrubber's
 * window shows which days are in view.
 */
export default function Filmstrip(props: FilmstripProps) {
    const {
        children,
        days,
        onDayChange,
        currentDay,
        currentImageId,
        denseThumbnails = false,
    } = props;

    const { t } = useTranslation();
    const trackRef = useRef<HTMLElement>(null);

    useRailGestures(trackRef);
    useLazyThumbnails(trackRef, denseThumbnails);
    useKeepCurrentCardInView(trackRef, currentImageId);
    const view = useRailView(trackRef);

    /* Shows the day's first card if the strip already holds it, and always hands the day on:
       the pages load one day at a time, so for them this is what brings the day's images. */
    const selectDay = (day: string) => {
        if (trackRef.current) {
            scrollToDay(trackRef.current, day);
        }
        onDayChange(day);
    };

    const page = (direction: -1 | 1) => {
        if (trackRef.current) {
            scrollPage(trackRef.current, direction);
        }
    };

    return (
        <div className={styles.filmstrip}>
            <div className={styles.roundMask} />
            <div className={styles.rail}>
                <span className={styles.fade} data-side="start" data-shown={!view.atStart} />
                <span className={styles.fade} data-side="end" data-shown={!view.atEnd} />
                {/* aria-disabled rather than disabled at the ends, so a keyboard user who pages
                    to the end keeps focus on the button they pressed. */}
                <Button
                    className={classNames(styles.pageButton, styles.pageStart)}
                    variant="secondary"
                    size="small"
                    iconOnly
                    aria-label={t('imageViewer.filmstrip.scrollLeft')}
                    title={t('imageViewer.filmstrip.scrollLeft')}
                    aria-disabled={view.atStart}
                    onClick={view.atStart ? undefined : () => page(-1)}
                    icon={
                        <SvgIcon
                            icon={ArrowLeftSLine}
                            size="inherit"
                            svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                        />
                    }
                />
                <Button
                    className={classNames(styles.pageButton, styles.pageEnd)}
                    variant="secondary"
                    size="small"
                    iconOnly
                    aria-label={t('imageViewer.filmstrip.scrollRight')}
                    title={t('imageViewer.filmstrip.scrollRight')}
                    aria-disabled={view.atEnd}
                    onClick={view.atEnd ? undefined : () => page(1)}
                    icon={
                        <SvgIcon
                            icon={ArrowRightSLine}
                            size="inherit"
                            svgProps={{ 'aria-hidden': true, 'focusable': 'false' }}
                        />
                    }
                />
                <nav
                    ref={trackRef}
                    className={styles.track}
                    aria-label={t('imageViewer.filmstrip.label')}>
                    {children}
                </nav>
            </div>
            <DayScrubber
                days={days}
                currentDay={currentDay}
                visibleDays={view.visibleDays}
                onSelectDay={selectDay}
            />
        </div>
    );
}
